/**
 * What the case dashboard renders (PRD-0075 R-D2): the view the feature reads and maps, and the
 * small pure helpers the pieces share. Months are `YYYY-MM` in UTC.
 */
import { dateTimeFormat, numberFormat } from '@/components/humint/format'
import type { AlertInboxRow } from '@/components/monitor/AlertInbox'
import type { StandingSourceRow } from '@/components/monitor/SourcesPane'
import type { HypothesisTrend, SourceKind } from '@/services/api/case-dashboard'
import type { HypothesisConfidence, HypothesisVerdict } from '@/types/analytic-hypothesis'

export type DashboardTileKey = 'sources' | 'newObjects' | 'alerts'

export interface DashboardTileView {
  key: DashboardTileKey
  total: number
  last30Days: number
  /** One count per month of the 12-month window, oldest first. */
  sparkline: readonly { month: string; count: number }[]
}

/** One month of the chart: sources learned by kind and the alerts raised. */
export interface DashboardMonthView {
  month: string
  sources: Readonly<Record<SourceKind, number>>
  alerts: number
}

/** A verdict and how firmly it was given; what the hypotheses' badges show. */
export interface VerdictPoint {
  verdict: HypothesisVerdict
  confidence: HypothesisConfidence
}

/** An open hypothesis as it stood: its latest assessment (null: none yet) and the trend into it. */
export interface DashboardHypothesisView {
  id: string
  title: string
  /** The hypothesis page. */
  href: string
  point: VerdictPoint | null
  /** ISO 8601. */
  assessedAt: string | null
  /** Null until there are two assessments. */
  trend: HypothesisTrend | null
  previousVerdict: HypothesisVerdict | null
}

/** The newest judgment in words: an assessment's rationale, or an investigation's assessment note. */
export interface DashboardNoteView {
  text: string
  authorName: string
  /** ISO 8601. */
  writtenAt: string
  /** What it judges: the hypothesis, else the investigation it was written on, with its page. */
  about: { kind: 'hypothesis' | 'investigation'; title: string; href: string } | null
  /** An assessment's verdict; a note has none. */
  point: VerdictPoint | null
}

/** A watched entity and when the case last learned a mention of it; null when it had not by then. */
export interface DashboardWatchedView {
  uid: string
  label: string | null
  entityType: string | null
  /** ISO 8601. */
  lastSeenAt: string | null
}

/** The lists beside the chart (PRD-0075 R-D2), as of the dashboard's date. */
export interface DashboardPanelsView {
  hypotheses: readonly DashboardHypothesisView[]
  latestNote: DashboardNoteView | null
  /** The top of the Monitor inbox. */
  whatsNew: readonly AlertInboxRow[]
  watched: readonly DashboardWatchedView[]
  sources: readonly StandingSourceRow[]
  /** Where the lists are in full: the Investigations page (hypotheses) and the Monitor. */
  links: { hypotheses: string; monitor: string }
}

export interface CaseDashboardModel {
  name: string
  /** ISO 8601. */
  openedAt: string
  lastIntakeAt: string | null
  tiles: readonly DashboardTileView[]
  /** The stack, bottom first. */
  sourceKinds: readonly SourceKind[]
  months: readonly DashboardMonthView[]
  panels: DashboardPanelsView
}

/** *Spis k dátumu* in the dashboard header: the day the file is read as of (null: now), and its change. */
export interface DashboardAsOf {
  day: string | null
  onChange: (day: string | null) => void
}

export type CaseDashboardState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; dashboard: CaseDashboardModel }

/**
 * Each kind keeps its colour whatever else is on screen (theme tokens `--cat-*`). The order was
 * validated with the dataviz palette check as adjacent pairs, light and dark: worst CVD ΔE 8.9,
 * worst normal-vision ΔE 27.5. Two light-mode fills sit under 3:1 on white, so the chart ships a
 * table view.
 */
export const SOURCE_KIND_COLOR: Readonly<Record<SourceKind, string>> = {
  document: 'var(--color-cat-9)',
  table: 'var(--color-cat-4)',
  chat: 'var(--color-cat-2)',
  recording: 'var(--color-cat-7)',
  humint: 'var(--color-cat-1)',
  osint: 'var(--color-cat-8)',
}

/** The alert line: ink, not a series hue, so it never reads as one more kind of source. */
export const ALERT_LINE_COLOR = 'var(--color-foreground)'

/** The 1st of a `YYYY-MM` month as a UTC date. */
function monthStart(month: string): Date {
  const [year, number] = month.split('-').map(Number)
  return new Date(Date.UTC(year, number - 1, 1))
}

const LONG_MONTH = { month: 'long', year: 'numeric', timeZone: 'UTC' } as const
const SHORT_MONTH = { month: 'short', timeZone: 'UTC' } as const
const YEAR = { year: 'numeric', timeZone: 'UTC' } as const

/** "October 2026" / "říjen 2026" for a `YYYY-MM` month. */
export function formatMonth(month: string, locale: string): string {
  return dateTimeFormat(locale, LONG_MONTH).format(monthStart(month))
}

/** An axis tick's lines: the short month, and the year under it on January and on the first tick. */
export function monthTickLines(month: string, locale: string, first: boolean): string[] {
  const start = monthStart(month)
  const short = dateTimeFormat(locale, SHORT_MONTH).format(start)
  if (!first && !month.endsWith('-01')) return [short]
  return [short, dateTimeFormat(locale, YEAR).format(start)]
}

/** Months a quarter, half-year or year apart (January always among them), at most ~10 ticks. */
export function axisMonths(months: readonly string[]): string[] {
  const step = [1, 2, 3, 6, 12].find(n => months.length / n <= 10) ?? 12
  return months.filter(month => (Number(month.slice(5, 7)) - 1) % step === 0)
}

export function formatCount(value: number, locale: string): string {
  return numberFormat(locale, {}).format(value)
}
